<template>
  <v-card class="add-purchase-order" height="90vh">
    <v-card-title>{{ $t('admin_acc_purchase_order.add_title') }}</v-card-title>
    <v-card-text>
      <div class="add-purchase-order-content" v-if="ready">
        <div class="add-purchase-order-content-col left">
          <v-form ref="form">
            <v-input :rules="requiredRules"
                     v-model="purchaseOrder.issued_at">
              <v-container fluid class="flex-column pa-0">
                <legend class="v-label theme--light">{{ $t('admin_acc_purchase_order.issued_at') }}</legend>
                <DatePickerInput v-model="purchaseOrder.issued_at"
                                 hide-details></DatePickerInput>
              </v-container>
            </v-input>
            <v-input :rules="requiredRules"
                     v-model="purchaseOrder.due_at">
              <v-container fluid class="flex-column pa-0">
                <legend class="v-label theme--light">{{ $t('admin_acc_purchase_order.due_at') }}</legend>
                <DatePickerInput v-model="purchaseOrder.due_at"
                                 hide-details></DatePickerInput>
              </v-container>
            </v-input>

            <v-autocomplete outlined
                            :label="$t('admin_acc_purchase_order.recipient')"
                            :items="userOptions"
                            v-model="purchaseOrder.recipient_id"
                            :rules="requiredRules">
            </v-autocomplete>
            <v-text-field outlined
                          :label="$t('admin_acc_purchase_order.title')"
                          v-model="purchaseOrder.title"
                          :rules="requiredRules"></v-text-field>
            <v-text-field outlined
                          :label="$t('admin_acc_purchase_order.amount')"
                          type="number"
                          step=".01"
                          min="0"
                          prefix="$"
                          v-model="purchaseOrder.amount"></v-text-field>
            <v-textarea outlined
                        :label="$t('admin_acc_purchase_order.remarks')"
                        v-model="purchaseOrder.remarks"></v-textarea>
          </v-form>
        </div>
        <div class="add-purchase-order-content-col right">
          <v-row class="flex-grow-0" no-gutters>
            <v-btn class="ml-2"
                   color="primary"
                   @click="selectedBookingIds = [...bookingIds]">{{ $t('admin_acc_purchase_order.select_all_bookings') }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn class="ml-2"
                   color="primary"
                   :disabled="selectedBookingIds.length === 0"
                   @click="removeBookings">{{ $t('admin_acc_purchase_order.remove_bookings') }}
            </v-btn>
            <v-btn class="ml-2"
                   color="primary"
                   @click="addBookings">{{ $t('admin_acc_purchase_order.add_bookings') }}
            </v-btn>
            <v-btn class="ml-2"
                   color="primary"
                   @click="addRecurrentBookings">{{ $t('admin_acc_purchase_order.add_recurrent_bookings') }}
            </v-btn>
          </v-row>
          <AdminBookingList full-height
                            hide-actions
                            hide-search
                            show-select
                            v-model="selectedBookingIds"
                            :extra-filters="{'id': bookingIds}"></AdminBookingList>
        </div>
      </div>
    </v-card-text>
    <v-card-actions>
      <v-btn color="secondary"
             @click="$emit('submit')">
        {{ $t('close') }}
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn color="primary"
             @click="$refs.form.validate() && submit()">
        {{ $t('ok') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
<script>
import {computed, onMounted, ref} from "@vue/composition-api";
import AdminBookingList from "@/components/acc_booking/booking/AdminBookingList.vue";
import DatePickerInput from "@/components/DatePickerInput.vue";
import SelectBookingModal from "@/components/acc_booking/booking/SelectBookingModal.vue";
import SelectRecurrentBookingModal from "@/components/acc_booking/booking/SelectRecurrentBookingModal.vue";

export default {
  name: 'AddPurchaseOrderModal',
  components: {DatePickerInput, AdminBookingList, EditMemberModal: () => import('@/components/member/EditMemberModal')},
  props: {
    bookingRecordId: String,
  },
  setup(props, {root, emit}) {
    const ready = ref(false);

    const requiredRules = ref([
      value => !!value || root.$t('register.required_fields'),
    ]);
    const purchaseOrder = ref({
      amount: 0,
    });

    const bookingIds = ref([]);
    const selectedBookingIds = ref([]);

    // Recipient
    const users = ref([]);
    const userOptions = computed(() => {
      return users.value.map((u) => {
        return {text: `${u.id.toString().padStart(6, '0')} - ${root.$t('name', {given_name: u.given_name, surname: u.surname})}`, value: u.account};
      })
    });
    const getUsers = function () {
      root.$callAPI('hkiarace/admin/user/', {page_size: -1}, function (response) {
        users.value = response.body.users;
      });
    };

    const removeBookings = function () {
      bookingIds.value = _.filter(bookingIds.value, (b) => selectedBookingIds.value.indexOf(b) === -1)
      selectedBookingIds.value = [];
    }
    const addBookings = async function () {
      const addedBookingIds = await root.$dialog.showAndWait(SelectBookingModal, {
        persistent: true,
        width: '95vw',
        height: '90vh',
      });

      if (addedBookingIds !== undefined) {
        bookingIds.value = _.uniq([...bookingIds.value, ...addedBookingIds]);
      }
    }

    const addRecurrentBookings = async function () {
      const bookingRecordIds = await root.$dialog.showAndWait(SelectRecurrentBookingModal, {
        persistent: true,
        width: '95vw',
        height: '90vh',
      });

      if (bookingRecordIds !== undefined) {
        root.$callAPI('aafb/admin/bookings/', {
          filters: {'booking_record.id': bookingRecordIds},
          page_no: 1,
          page_size: -1,
        }, function (response) {
          const addedBookingIds = response.body.bookings.map((b) => b.id)

          bookingIds.value = _.uniq([...bookingIds.value, ...addedBookingIds]);
        });
      }
    }

    const submit = function () {
      root.$callAPI('aafb/admin/purchase_order/create/', {
        ...purchaseOrder.value,
        amount: Math.floor(parseFloat(purchaseOrder.value.amount) * 100),
        booking_ids: bookingIds.value,
      }, function (response) {
        emit('submit', true);
      })
    }

    onMounted(() => {
      getUsers()

      if (props.bookingRecordId) {
        root.$callAPI('aafb/admin/bookings/', {
          page_size: -1,
          filters: {'booking_record.id': [props.bookingRecordId], 'purchase_order': false},
        }, async (response) => {
          bookingIds.value = response.body.bookings.map((b) => b.id);
          ready.value = true;
        })
      } else {
        ready.value = true;
      }
    })

    return {
      ready,

      requiredRules,
      purchaseOrder,

      bookingIds,
      selectedBookingIds,

      userOptions,

      removeBookings,
      addBookings,
      addRecurrentBookings,

      submit,
    }
  }
}
</script>

<style lang="less">
.add-purchase-order {
  display: flex !important;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .v-card__title, .v-card__text, .v-card__actions {
    width: 100%;
  }

  .v-card__title, .v-card__actions {
    flex: 0 0 auto;
  }

  .v-card__text {
    flex: 1 1 0;
    overflow: auto;
  }

  .add-purchase-order-content {
    width: 100%;
    height: 100%;

    display: flex;
    gap: 12px;

    .add-purchase-order-content-col {
      height: 100%;

      display: flex;
      flex-direction: column;
      overflow-y: auto;

      &.left {
        flex: 1 1 0;
      }

      &.right {
        flex: 2 2 0;
      }
    }
  }
}


</style>
